/**
 * ชนิดข้อมูลของหน้า "แผนการผลิต" (`WO-P2-M5-006`, D-127/D-110 ข้อ 8) — เขียนตาม
 * Pydantic schema จริงที่เซิร์ฟเวอร์ serve (`apps/api/src/cap_erp_api/modules/
 * planning/schemas.py`) ตรวจกับ router จริง (`.../planning/router.py`) ก่อนเขียน
 * ไฟล์นี้ ไม่ใช่จาก `docs/API_CONTRACT.md` เพียงอย่างเดียว
 *
 * หน้าจอนี้กินสองเส้นที่มีอยู่แล้วทั้งคู่ (D-110 ข้อ 1): `GET /v1/mps-versions`
 * (เลือกแผน) และ `GET /v1/mps-attainment` (แผนเทียบของจริง) — **ไม่มี `POST`/
 * `PATCH` ใดในไฟล์นี้เลย** การสร้าง/แก้ MPS เป็นงานของหน้าจออื่นที่ยังไม่เกิด
 *
 * **ฝาแฝดของ `lib/mrp/types.ts` ไม่ใช่ผู้ใช้ของมัน** — แม้ทั้งคู่จะอยู่ในโมดูลสิทธิ์
 * `PLANNING` เดียวกัน (D-093) ค่าคงที่ที่ควรจะเท่ากันถูกประกาศซ้ำที่นี่โดยตั้งใจ
 * ผูกกันด้วยค่าที่บังเอิญเท่ากัน ไม่ใช่ import ข้ามโดเมน (เหตุผลเดียวกับที่
 * `lib/mrp/types.ts` เขียนไว้)
 *
 * สามข้อที่กำหนดทั้งหน้าจอ และทุกข้อมาจาก contract/router ไม่ใช่รสนิยม:
 *
 * 1. **ตัวเลขทั้งหมดเป็นสตริงที่สเกล `NUMERIC(18, 4)`** (D-095/D-099/D-110) ยกเว้น
 *    `attainment_pct` ซึ่งสเกล 2 ตำแหน่ง — ห้ามแปลงเป็น `number` ที่ไหนเลย
 * 2. **`attainment_pct` เป็น `null` ได้เมื่อแผนเป็นศูนย์** (D-110 ข้อ 7) และเป็น
 *    `null` คนละความหมายกับ `"0.00"` — หน้าจอต้องแยกสองค่านี้ให้ชัด ไม่ใช่กลืนเป็น
 *    ค่าเดียวกัน
 * 3. **"คงเหลือที่ต้องผลิต" ไม่มีอยู่ในชนิดนี้เลย** — มันคำนวณที่หน้าจอ (`mpsView.ts`)
 *    จาก `planned_qty`/`actual_good_qty` และไม่ถูกส่งไปเซิร์ฟเวอร์ (D-127 ข้อ 3)
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — เดียวกับ Forecast/MPS/MRP (D-093) */
export const PLANNING_MODULE = 'PLANNING';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_MPS_STATUS = 'DRAFT';
export const APPROVED_MPS_STATUS = 'APPROVED';
export const FROZEN_MPS_STATUS = 'FROZEN';
export const RELEASED_MPS_STATUS = 'RELEASED';
export const CANCELLED_MPS_STATUS = 'CANCELLED';

/** ห้าสถานะทั้งหมดที่คอลัมน์นี้มี (D-096) — `DRAFT → APPROVED → FROZEN → RELEASED`
 * พร้อม `CANCELLED` ที่ไปได้จากทุกสถานะยกเว้นสองตัวสุดท้าย */
export const MPS_VERSION_STATUSES = [
  DRAFT_MPS_STATUS,
  APPROVED_MPS_STATUS,
  FROZEN_MPS_STATUS,
  RELEASED_MPS_STATUS,
  CANCELLED_MPS_STATUS,
] as const;

export type MpsVersionStatus = (typeof MPS_VERSION_STATUSES)[number];

/* ------------------------------------------------------------ กฎ (rule) -- */

/** `422` — `date_from` อยู่หลัง `date_to` */
export const MPS_ATTAINMENT_RANGE_IS_REVERSED = 'MPS_ATTAINMENT_RANGE_IS_REVERSED';
/** `422` — ช่วงที่ขอ (หลังตัดเข้า horizon แล้ว) กว้างเกิน 366 วัน */
export const MPS_ATTAINMENT_RANGE_TOO_WIDE = 'MPS_ATTAINMENT_RANGE_TOO_WIDE';

/* ------------------------------------------------------------- resource --- */

/**
 * หนึ่งแผนบนรายการ (D-096) — รูปของแต่ละ item ใน `GET /v1/mps-versions`
 *
 * **ไม่มี `lines`** เหมือน `MpsVersionListItemResponse` ฝั่งเซิร์ฟเวอร์ — หน้าจอนี้
 * ไม่เคยอ่านบรรทัดของแผนเลย มันอ่านผลเทียบจาก `GET /v1/mps-attainment` แทน
 */
export interface MpsVersionSummary {
  readonly mps_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly version_label: string;
  /** หนึ่งใน `MPS_VERSION_STATUSES` */
  readonly status: string;
  readonly horizon_start: string;
  readonly horizon_end: string;
  readonly frozen_until: string | null;
  readonly created_by_employee_id: string;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly released_at: string | null;
  readonly cancelled_at: string | null;
  readonly note: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/mps-versions` — envelope มาตรฐานของทุกกลุ่มในระบบนี้ */
export interface MpsVersionListPage {
  readonly items: readonly MpsVersionSummary[];
  readonly next_cursor: string | null;
}

/**
 * หนึ่งแถว (สินค้า, วัน) ของแผนหนึ่งใบ — รูปของแต่ละ item ใน `GET /v1/mps-attainment`
 *
 * **ไม่มี id และไม่มี timestamp** (D-110 ข้อ 1) — ไม่มีอะไรถูกเก็บไว้ จึงไม่มีอะไรให้
 * address ทีหลัง ถามซ้ำหลัง Scan-Out จะได้คำตอบใหม่โดยไม่มีอะไรถูกอัปเดต
 */
export interface MpsAttainmentItem {
  readonly plant_code: string;
  readonly product_code: string;
  /** `YYYY-MM-DD` ล้วน */
  readonly bucket_date: string;
  /** สิ่งที่แผนสั่ง — `"0.0000"` บนแถวที่มีอยู่เพราะมีการผลิตที่แผนไม่ได้สั่ง */
  readonly planned_qty: string;
  /** `qty_good` ของ operation สุดท้าย — `"0.0000"` บนวันที่แผนสั่งแต่ไม่ได้ทำ */
  readonly actual_good_qty: string;
  /** `null` เมื่อ `planned_qty` เป็นศูนย์ (D-110 ข้อ 7) — ไม่ใช่ `0` และไม่ใช่ `∞` */
  readonly attainment_pct: string | null;
  /** D-133 ข้อ 1/2 — ทนเซิร์ฟเวอร์เก่า: ไม่มีฟิลด์นี้มา = `''` ไม่ใช่ invalid-response */
  readonly product_name: string;
  /** D-133 ข้อ 1/2 — `null` ได้ตามที่ Product Master เก็บ หรือเมื่อเซิร์ฟเวอร์เก่ายังไม่ส่งฟิลด์นี้ */
  readonly model: string | null;
}

/**
 * ตัวเลือก "Line ผลิต" หนึ่งแถวของ `work_centers` บนคำตอบ `GET /v1/mps-attainment`
 * (D-128 ข้อ 1/3) — Line = ศูนย์งานที่สินค้าของแผนนี้ผ่านใน Routing ปัจจุบัน ไม่ขึ้นกับ
 * ตัวกรอง/หน้าที่ขอ
 */
export interface MpsWorkCenterOption {
  readonly work_center_code: string;
  readonly name: string;
}

/** `GET /v1/mps-attainment` — envelope เดียวกัน เรียงตาม `(product_code, bucket_date)` */
export interface MpsAttainmentPage {
  readonly items: readonly MpsAttainmentItem[];
  readonly next_cursor: string | null;
  /** ตัวเลือก Line ของแผนนี้ — `[]` เมื่อเซิร์ฟเวอร์รุ่นเก่ายังไม่ส่งฟิลด์นี้ (D-128 ข้อ 3) */
  readonly work_centers: readonly MpsWorkCenterOption[];
}

/* -------------------------------------------- สร้างแผนใหม่ (WO-P2-M5-010, D-129) --- */

/** เท่ากับ `NOTE_LENGTH` ฝั่งเซิร์ฟเวอร์ (`planning/mps.py`) */
export const MPS_NOTE_MAX_LENGTH = 255;

/**
 * หนึ่งบรรทัดของ `POST /v1/mps-versions` (D-096) — **ไม่มี `line_no`** (ลำดับใน
 * อาร์เรย์) และ**ไม่มี `uom_code`** (ใช้หน่วยหลักของสินค้า) ตามที่ `MpsLineCreate`
 * ฝั่งเซิร์ฟเวอร์ freeze ไว้
 */
export interface MpsLineCreateInput {
  readonly product_code: string;
  readonly bucket_date: string;
  /** สตริงเสมอ — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ */
  readonly planned_qty: string;
}

/**
 * body ของ `POST /v1/mps-versions` — header และ lines ในคำขอเดียว (D-096)
 *
 * **`version_label` เป็นทางเลือก (D-132 ข้อ 2)** — ไม่ส่ง = เซิร์ฟเวอร์ออกเลขให้เอง
 * (`MPS-<plant>-<YYMM>-<4 หลัก>`) หน้าเว็บทุกหน้าไม่ส่งคีย์นี้อีกแล้ว แต่ type ยังรับ
 * ค่าได้เผื่อผู้เรียกอื่น (helper ของ MRP/attainment ที่ยังส่งป้ายเอง) ไม่พัง
 */
export interface MpsVersionCreateBody {
  readonly plant_code: string;
  readonly version_label?: string;
  readonly horizon_start: string;
  readonly horizon_end: string;
  readonly note?: string;
  readonly lines: readonly MpsLineCreateInput[];
}

/* --------------------------------------- รายละเอียดแผน + ขับสถานะ (WO-P2-M5-011, D-129) --- */

/** หนึ่งบรรทัดที่เก็บไว้จริง — รูปของแต่ละ item ใน `lines` ของ `MpsVersionResponse` (D-096) */
export interface MpsLineDetail {
  readonly line_no: number;
  readonly product_id: string;
  readonly product_code: string;
  readonly bucket_date: string;
  /** สตริงเสมอ — `NUMERIC(18, 4)` */
  readonly planned_qty: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * แผนหนึ่งใบพร้อมทุกบรรทัด — `MpsVersionResponse` ของ `GET /{mps_id}` **และคำตอบ
 * ของทั้งสี่ transition** (`approve`/`freeze`/`release`/`cancel`) ซึ่งคืนรูปเดียวกัน
 * เป๊ะ (D-096: "The same shape answers the create, the record read and all four
 * transitions")
 */
export interface MpsVersionDetail extends MpsVersionSummary {
  readonly lines: readonly MpsLineDetail[];
}

/** body ของ `POST /{mps_id}/freeze` — ฟิลด์เดียว บังคับเสมอ (D-096) */
export interface MpsFreezeBody {
  readonly frozen_until: string;
}
